import PriceLabelLifetime from 'UI/PriceLabelLifetime';
import { TermEnum } from 'UI/PriceLabelLifetime/PriceLabelLifetime';
import { SubscribeDtoTypeEnum } from 'api/generated';
import { ActivePlan, Plan } from 'components/Modals/ChooseProductModal/components/Products/types';
import { FC } from 'react';
import styled, { css } from 'styled-components';
import { getReducedPrice } from 'utils';

interface PriceSectionProps {
  term: TermEnum;
  installmentPriceLifetime: number;
  subscribeType: SubscribeDtoTypeEnum;
  termsCount: number;
  mainColor: string | undefined;
  planType: ActivePlan | undefined;
  isResponsive: boolean;
  goldCount?: number;
  countPlans?: number;
  isMaxHeight?: boolean | undefined;
  onChoosePlan: any;
  isCurrentSubscribe: boolean;
  currentPriceLifetime: number;
  currentPaymentPeriod: string | undefined;
  isGoldSubscribe: boolean;
  isSelect: boolean;
  plan: any;
  showBothPrices?: boolean;
  isPaymentPlan?: boolean;
}

export const PriceSection: FC<PriceSectionProps> = ({
  isResponsive,
  subscribeType,
  mainColor,
  termsCount,
  planType,
  isCurrentSubscribe,
  currentPaymentPeriod,
  onChoosePlan,
  installmentPriceLifetime,
  currentPriceLifetime,
  isSelect,
  plan,
  showBothPrices,
  isPaymentPlan,
}) => {
  const calculateCurrentPrice = (
    amount: number,
    defaultCouponDiscount?: number,
    defaultCouponDiscountType?: string,
  ) => {
    if (defaultCouponDiscount && amount - defaultCouponDiscount >= 1 && defaultCouponDiscountType === 'amount') {
      return getReducedPrice(amount - defaultCouponDiscount);
    }

    if (defaultCouponDiscount && defaultCouponDiscountType === 'percentage') {
      const discountAmount = amount * (defaultCouponDiscount / 100);

      if (currentPriceLifetime - discountAmount >= 1) {
        return getReducedPrice(amount - discountAmount);
      }
    }

    return getReducedPrice(amount);
  };

  console.log('🔍 PriceSection Debug:', {
    subscribeType,
    isPaymentPlan,
    showBothPrices,
    hasPurchasePrice: !!plan?.purchasePrice,
    hasInstallmentPrice: !!plan?.installmentPrice,
    purchasePrice: plan?.purchasePrice,
    installmentPrice: plan?.installmentPrice,
    shouldShowPurchase: plan?.purchasePrice && (!isPaymentPlan || showBothPrices),
    shouldShowInstallment: plan?.installmentPrice && (isPaymentPlan || showBothPrices),
    conditionForPurchase: `${!!plan?.purchasePrice} && (${!isPaymentPlan} || ${!!showBothPrices})`,
    conditionForInstallment: `${!!plan?.installmentPrice} && (${!!isPaymentPlan} || ${!!showBothPrices})`,
  });

  return (
    <>
      {/* Show purchase price when: one-time payment is selected OR showBothPrices is true */}
      {plan?.purchasePrice && (!isPaymentPlan || showBothPrices) && (
        <PriceWrapper isResponsive={isResponsive}>
          {isCurrentSubscribe && currentPaymentPeriod === TermEnum.LIFE_TIME && <YourPlan>Your plan</YourPlan>}
          <PriceContainer
            isSelect={isSelect}
            termCount={termsCount}
            mainColor={mainColor}
            activePlan={planType}
            subscribeType={subscribeType}
            term={TermEnum.LIFE_TIME}
            isResponsive={isResponsive}
            onClick={() => onChoosePlan(plan.purchasePrice as Plan, TermEnum.LIFE_TIME, subscribeType)}>
            <StyledPriceLabel
              isResponsive={isResponsive}
              currentPrice={calculateCurrentPrice(
                currentPriceLifetime || 0,
                //@ts-ignore
                plan?.purchasePrice?.defaultCoupon?.discountAmount,
                //@ts-ignore
                plan?.purchasePrice?.defaultCoupon?.discountType,
              )}
              //@ts-ignore
              oldPrice={plan?.purchasePrice?.defaultCoupon?.discountAmount ? currentPriceLifetime || 0 : undefined}
              term={TermEnum.LIFE_TIME}
            />
          </PriceContainer>
        </PriceWrapper>
      )}
      {/* Show installment price when: payment plan is selected OR showBothPrices is true */}
      {plan?.installmentPrice && (isPaymentPlan || showBothPrices) && (
        <PriceWrapper isResponsive={isResponsive}>
          {isCurrentSubscribe && currentPaymentPeriod === TermEnum.LIFE_TIME && <YourPlan>Your plan</YourPlan>}
          <PriceContainer
            isSelect={isSelect}
            termCount={termsCount}
            mainColor={mainColor}
            activePlan={planType}
            subscribeType={subscribeType}
            term={TermEnum.LIFE_TIME}
            isResponsive={isResponsive}
            onClick={() => onChoosePlan(plan.installmentPrice as Plan, TermEnum.LIFE_TIME, subscribeType)}>
            <StyledPriceLabel
              isResponsive={isResponsive}
              currentPrice={calculateCurrentPrice(
                installmentPriceLifetime || 0,
                //@ts-ignore
                plan?.installmentPrice?.defaultCoupon?.discountAmount,
                //@ts-ignore
                plan?.installmentPrice?.defaultCoupon?.discountType,
              )}
              //@ts-ignore
              oldPrice={
                plan?.installmentPrice?.defaultCoupon?.discountAmount ? installmentPriceLifetime || 0 : undefined
              }
              term={TermEnum.LIFE_TIME}
            />
          </PriceContainer>
        </PriceWrapper>
      )}
      {/* Show N/A for disabled Silver/Bronze installment plans */}
      {!plan?.installmentPrice &&
        (subscribeType === SubscribeDtoTypeEnum.Silver || subscribeType === SubscribeDtoTypeEnum.Bronze) &&
        (isPaymentPlan || showBothPrices) && (
          <PriceWrapper isResponsive={isResponsive}>
            <PriceContainer
              isSelect={false}
              termCount={termsCount}
              mainColor={mainColor}
              activePlan={planType}
              subscribeType={subscribeType}
              term={TermEnum.LIFE_TIME}
              isResponsive={isResponsive}
              onClick={() => {}}>
              <DisabledPriceLabel isResponsive={isResponsive}>N/A</DisabledPriceLabel>
            </PriceContainer>
          </PriceWrapper>
        )}
    </>
  );
};

const PriceWrapper = styled.div<{ isResponsive: boolean }>`
  position: relative;
  width: ${({ isResponsive }) => (isResponsive ? '100%' : 'unset')};
`;

const PriceContainer = styled.div<{
  termCount: number;
  activePlan: { planType?: string; term: string } | undefined;
  subscribeType: string;
  mainColor: string | undefined;
  term: string;
  isSelect?: boolean;
  isResponsive: boolean;
}>`
  padding: ${({ subscribeType }) => (subscribeType === 'gold' ? '45px 4px' : '59px 8px')};
  background: ${({ theme: { colors } }) => colors.neutrals[9]};
  border: ${({ subscribeType, activePlan, mainColor, isSelect, term, theme: { colors } }) => {
    // For Silver and Bronze N/A boxes, use background color for seamless appearance
    if (subscribeType === 'silver' || subscribeType === 'bronze') {
      return `2px solid ${colors.neutrals[9]}`;
    }
    return (activePlan && subscribeType === activePlan.planType && term === activePlan.term) || isSelect
      ? `2px solid ${mainColor}`
      : `2px solid ${colors.neutrals[9]}`;
  }};
  border-radius: 12px;
  align-items: center;
  margin: 12px 0px;
  display: ${({ isResponsive }) => (isResponsive ? 'flex' : 'block')};
  justify-content: ${({ isResponsive }) => (isResponsive ? 'center' : 'unset')};
`;

const StyledPriceLabel = styled(PriceLabelLifetime)<{
  isResponsive: boolean;
}>`
  ${({ theme: { colors, typography } }) => css`
    gap: 8px;

    color: ${colors.neutrals[1]};
    ${typography.headline_semibold_18};
  `}

  display: ${({ isMaxHeight, goldCount }) => (isMaxHeight && goldCount === 1 ? 'flex' : 'flex')};
  flex-direction: ${({ isMaxHeight, goldCount }) => (isMaxHeight && goldCount === 1 ? 'column' : 'unset')};

  @media (max-width: 560px) {
    ${({ theme: { typography } }) => css`
      ${typography.body_large_bold_16}
      font-weight: 600;
      line-height: 14px;
    `}
  }
`;

const DisabledPriceLabel = styled.div<{
  isResponsive: boolean;
}>`
  ${({ theme: { colors, typography } }) => css`
    gap: 8px;
    color: ${colors.neutrals[1]};
    ${typography.headline_semibold_18};
  `}

  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 48px; /* Add height to match Gold pricing with coupons/discounts */

  @media (max-width: 560px) {
    ${({ theme: { typography } }) => css`
      ${typography.body_large_bold_16}
      font-weight: 600;
      line-height: 14px;
    `}
    min-height: 48px;
  }
`;

const YourPlan = styled.p`
  ${({ theme: { typography } }) => typography.footnote_semibold_12}
  background: ${({ theme: { colors } }) => colors.primary[1]};
  padding: 4px 8px;
  border-radius: 12px;
  width: 82px;
  color: ${({ theme: { colors } }) => colors.neutrals[11]};
  position: absolute;
  text-align: center;
  top: -15px;
  right: 5px;
`;
